import { BlockFilledIcon, DextHorizontalLogo, DotsFilledIcon } from '@approvalmax/ui';
import { Alert, Box, Button, Dropdown, Flex, Menu, Text } from '@approvalmax/ui/src/components';
import { domHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { memo } from 'react';

import { savingOrganisationState } from '../OrganisationInfo/OrganisationInfo.state';
import { qa } from './AdditionalServiceCardDext.constants';
import {
    useAdditionalServiceCardDext,
    useCompanyIntegrationStatus,
    useGetDextDescription,
    useGetDextLearnMoreUrl,
} from './AdditionalServiceCardDext.hooks';
import { messages } from './AdditionalServiceCardDext.messages';
import { AdditionalServiceCardDextProps } from './AdditionalServiceCardDext.types';

export const AdditionalServiceCardDext = memo<AdditionalServiceCardDextProps>((props) => {
    const { company } = props;

    const isSaving = useAtomValue(savingOrganisationState);
    const { isIntegrationConnected, isIntegrationDisconnected } = useCompanyIntegrationStatus(company);

    const { isLoading, onConnect, onReconnect, onDisconnect } = useAdditionalServiceCardDext(company);
    const learnMoreUrl = useGetDextLearnMoreUrl(company);
    const description = useGetDextDescription(company);

    const isConnected = company.receiptBankIntegration && company.receiptBankIntegration.isConnected;
    const isDisconnected = company.receiptBankIntegration && !company.receiptBankIntegration.isConnected;
    const isNeverConnected = !company.receiptBankIntegration;

    return (
        <Box
            color={isNeverConnected ? 'silver80' : 'white100'}
            bordered={!isNeverConnected}
            radius='xsmall'
            spacing='24'
            height='100%'
            aria-label={messages.dextPrepare}
            data-qa={domHelpers.generateDataQa(qa)}
        >
            <Flex direction='column' spacing='20' height='100%' justifyContent='space-between'>
                <Flex direction='column'>
                    <Flex spacing='16' alignItems='center'>
                        <Flex grow={1}>
                            <DextHorizontalLogo />
                        </Flex>

                        {isNeverConnected && (
                            <Button
                                size='small'
                                disabled={!isIntegrationConnected || isSaving || isLoading || company.isReadonly}
                                onClick={onConnect}
                                color='brandDext90'
                            >
                                {messages.connect}
                            </Button>
                        )}

                        {isConnected && !company.isReadonly && !company.flags.isWorkflowManager && (
                            <Dropdown
                                activator={
                                    <Button icon noPadding variant='text' color='blue100'>
                                        <DotsFilledIcon size={24} />
                                    </Button>
                                }
                                width={150}
                                disabled={isSaving || isLoading}
                            >
                                <Menu divider size='small'>
                                    <Menu.Item
                                        title={messages.disconnect}
                                        name={messages.disconnect}
                                        onClick={onDisconnect}
                                    />
                                </Menu>
                            </Dropdown>
                        )}

                        {isDisconnected && (
                            <Button
                                size='small'
                                disabled={!isIntegrationConnected || isSaving || isLoading || company.isReadonly}
                                onClick={onReconnect}
                                color='brandDext90'
                            >
                                {messages.reconnect}
                            </Button>
                        )}

                        {isConnected && (
                            <Text font='label' color='blue100' uppercase>
                                {messages.labelConnected}
                            </Text>
                        )}
                    </Flex>

                    <Flex direction='column' spacing='4'>
                        <Text
                            font='body'
                            fontSize='small'
                            color='midnight80'
                            data-qa={domHelpers.generateDataQa(qa, 'description')}
                        >
                            {description}
                        </Text>

                        <Text font='label' fontSize='small'>
                            <a href={learnMoreUrl} target='_blank' rel='noopener noreferrer'>
                                {messages.learnMore}
                            </a>
                        </Text>
                    </Flex>
                </Flex>

                {isIntegrationDisconnected && !isConnected && (
                    <Alert startIcon={<BlockFilledIcon size={24} />} size='small' invalid={true} color='transparent'>
                        {messages.warningConnectDisabled({ integrationName: company.integration?.displayName })}
                    </Alert>
                )}
            </Flex>
        </Box>
    );
});

AdditionalServiceCardDext.displayName = 'AdditionalServiceCardDext';
